import { useState } from "react";
import { useNavigate } from "react-router-dom";
import swal from "../../../../Shared/swal";
import {
  PostCreateOnlinePolicy,
  UploadOnlinePolicyDocuments,
  GetMakeModel,
  UpdateMakeModel,
} from "../../../../Service/_index";
import {
  ToastError,
  ToastSuccess,
} from "../../../../UiComponents/Toaster/Toast";
import { vehicleData } from "../OptionsData";
import {
  isNational,
  buildPolicySettings,
  settingsToLegacyFields,
  motorPremiumExcludingPa,
  coverageOf,
  selectionOfCoverage,
} from "../utils/companyRules";
import { boughtAgainKeys } from "../utils/partChanges";
import { normalizeChargeTo } from "../utils/editPayer";
import { keepsVehicle, hasSaved } from "../utils/masterDrift";
import { comboPayloadFields } from "../utils/vehicleSelection";
import { useOnlinePolicyConfig } from "./useOnlinePolicyConfig";
import { titleToSend } from "../utils/proposerTitle";

/**
 * Custom hook for managing online policy form state and actions
 */
export const useOnlinePolicyForm = (loadAllOnlinePolicies) => {
  const navigate = useNavigate();
  const { multiProductPolicy } = useOnlinePolicyConfig();
  const [showForm, setShowForm] = useState(false);
  const [selectedMakeModel, setSelectedMakeModel] = useState(null);
  // Multiple company only: the Combined Make & Model record picked. `selectedMakeModel`
  // stays the OD row; the TP row is `selectedCombo.tpRow`.
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [calculationResult, setCalculationResult] = useState(null);
  const [showCalculationResult, setShowCalculationResult] = useState(false);
  const [policyData, setPolicyData] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setViewMode] = useState(false);
  const [editPolicyData, setEditPolicyData] = useState(null);
  // The values an edited policy was saved with (null when creating) — see
  // utils/masterDrift. releaseSaved stops keeping a group ("vehicle" | "rto")
  // once the operator changes that pick.
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const releaseSaved = (...groups) =>
    setSavedSnapshot((prev) => {
      if (!prev || groups.every((g) => !prev.keep[g])) return prev;
      const keep = { ...prev.keep };
      groups.forEach((g) => {
        keep[g] = false;
      });
      return { ...prev, keep };
    });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setShowForm(false);
    setPolicyData(null);
    setSelectedMakeModel(null);
    setSelectedCombo(null);
    setShowCalculationResult(false);
    setCalculationResult(null);
    setIsEditMode(false);
    setViewMode(false);
    setEditPolicyData(null);
    setSavedSnapshot(null);
    navigate("/onlinePolicy");
  };

  const handleViewPolicyErrorLogs = (policyId) => {
    setViewMode(true);
    navigate(`/onlinePolicy/view/error_logs/${policyId}`);
  };

  const handleViewPolicy = (policyId) => {
    setViewMode(true);
    navigate(`/onlinePolicy/view/form/${policyId}`);
  };

  // the policy's History as a page (2026-10-22) - the customer's name rides along for the header
  const handleViewPolicyHistory = (policyId, name) => {
    setViewMode(true);
    navigate(`/onlinePolicy/view/history/${policyId}`, { state: { name } });
  };

  const saveCustomerDetails = async (
    customerData,
    policyData,
    isEditMode,
    editPolicyData,
    policyId,
    setOpenLoader,
    // createRequestId: one id per Create attempt (a repeat of the same request makes no second policy);
    // holdForPayment: save ON HOLD when a wallet is short; onInsufficientWallet(response): the server said a wallet is
    // short — the page offers Cancel / Hold instead of an error.
    extras = {},
  ) => {
    setOpenLoader(true);
    let lastResponse = null;

    try {
      let selectedVehicle;
      let vehicleMake, vehicleModel, vehicleCC, company, vehicleVariant;

      console.log("Debug vehicle selection:", {
        isEditMode,
        selectedMakeModel,
        policyDataMakeModel: policyData.makeModel,
        editPolicyDataVehicleMake: editPolicyData?.vehicleMake,
        editPolicyDataVehicleModel: editPolicyData?.vehicleModel,
      });

      // Extract vehicleMake, vehicleModel, and company from selectedMakeModel (from /api/make-model)
      // If selectedMakeModel exists, use it; otherwise fall back to old vehicleData
      if (selectedMakeModel && typeof selectedMakeModel === "object") {
        // New structure from /api/make-model
        if (selectedMakeModel.make && selectedMakeModel.model) {
          vehicleMake = selectedMakeModel.make;
          vehicleModel = selectedMakeModel.model;
          vehicleCC = parseInt(selectedMakeModel.cc) || 0;
          company = selectedMakeModel.company || "";
          vehicleVariant = selectedMakeModel.vehicleVariant || "";
        }
        // Old structure from vehicleData
        else if (
          selectedMakeModel.VehicleMake &&
          selectedMakeModel.VehicleModel
        ) {
          vehicleMake = selectedMakeModel.VehicleMake;
          vehicleModel = selectedMakeModel.VehicleModel;
          vehicleCC = selectedMakeModel.CC || 0;
          // Use company from editPolicyData if in edit mode, otherwise empty string
          company = isEditMode ? editPolicyData?.company || "" : "";
        }
      } else if (isEditMode) {
        // Fallback for edit mode
        selectedVehicle = vehicleData.find(
          (v) => v.ID_PK === policyData.makeModel
        );
        vehicleMake =
          selectedVehicle?.VehicleMake || editPolicyData?.vehicleMake || "";
        vehicleModel =
          selectedVehicle?.VehicleModel || editPolicyData?.vehicleModel || "";
        vehicleCC = selectedVehicle?.CC || editPolicyData?.vehicleCC || 0;
        company = editPolicyData?.company || "";
      } else {
        // Fallback for create mode
        selectedVehicle = vehicleData.find(
          (v) => v.ID_PK === policyData.makeModel
        );
        vehicleMake = selectedVehicle?.VehicleMake || "";
        vehicleModel = selectedVehicle?.VehicleModel || "";
        vehicleCC = selectedVehicle?.CC || 0;
        // company = ""; // Removed to allow policyData.company to take precedence
      }

      // Prioritize company from form data (policyData)
      if (policyData.company) {
        company = policyData.company;
      } else if (
        !company &&
        selectedMakeModel &&
        typeof selectedMakeModel === "object"
      ) {
        company = selectedMakeModel.company;
      } else if (!company && isEditMode && editPolicyData?.company) {
        company = editPolicyData.company;
      }

      // If company is still missing and we have a makeModel ID, try to fetch it from API
      if (!company && policyData.makeModel) {
        try {
          const makeModelResponse = await GetMakeModel();
          if (makeModelResponse && makeModelResponse.data) {
            const makeModelId = policyData.makeModel.toString();
            const matchingMakeModel = makeModelResponse.data.find(
              (item) => item._id?.toString() === makeModelId
            );
            if (matchingMakeModel && matchingMakeModel.company) {
              company = matchingMakeModel.company;
              vehicleVariant = matchingMakeModel.vehicleVariant || "";
            }
          }
        } catch (error) {
          console.error("Error fetching make model for company:", error);
        }
      }

      // An edited policy keeps the vehicle it was SOLD with while the vehicle is
      // unchanged — the master's current make / model / CC / variant must not
      // replace it.
      if (isEditMode && keepsVehicle(savedSnapshot, policyData.makeModel)) {
        vehicleMake = savedSnapshot.vehicleMake ?? vehicleMake;
        vehicleModel = savedSnapshot.vehicleModel ?? vehicleModel;
        vehicleCC = savedSnapshot.vehicleCC ?? vehicleCC;
        if (hasSaved(savedSnapshot.vehicleVariant)) {
          vehicleVariant = savedSnapshot.vehicleVariant;
        }
      }

      console.log("Final vehicle details:", {
        selectedVehicle,
        vehicleMake,
        vehicleModel,
        vehicleCC,
        company,
        vehicleVariant,
      });

      const firstFiniteNumber = (...candidates) => {
        for (const candidate of candidates) {
          if (candidate === null || candidate === undefined || candidate === "")
            continue;
          const num = Number(candidate);
          if (Number.isFinite(num)) return num;
        }
        return undefined;
      };

      // Motor premium WITHOUT the CPA, GST included. The calculator folds the PA
      // amount into Total B (PA to Owner Driver), so its finalPremium already
      // holds PA + 18% GST; taking it as-is made the wallet charge PA twice
      // (once inside "motor", once as PA). Back it out — same as the Basic
      // step's own "Motor Premium" summary line.
      const resolvedMotorPremium = firstFiniteNumber(
        motorPremiumExcludingPa(calculationResult?.finalPremium, policyData),
        policyData?.originalFinalPremium,
        policyData?.finalPremium,
        editPolicyData?.originalFinalPremium,
        editPolicyData?.finalPremium
      );

      // Pre-tax net. Was previously read off customerData, which has no such
      // field at all — so every policy stored netPremium: 0.
      const resolvedNetPremium = firstFiniteNumber(
        calculationResult?.netPremiumAB,
        policyData?.netPremium,
        editPolicyData?.netPremium
      );

      const resolvedTax = firstFiniteNumber(calculationResult?.tax);

      // CPA carries its own 18% GST — the same rule the Basic step summary and
      // the Preview both display.
      const paCoverBase = parseFloat(policyData.paCoverAmount) || 0;
      const paCoverWithGst = policyData.paCover
        ? Number((paCoverBase * 1.18).toFixed(2))
        : 0;

      // What the customer actually pays, and what the policy is worth. This is
      // the figure the Preview shows as "Final Premium" and the list shows in
      // its Premium column.
      const resolvedTotalPremium = Number(
        ((resolvedMotorPremium || 0) + paCoverWithGst).toFixed(2)
      );

      const routing = buildPolicySettings(
        { ...policyData, company },
        { multiProduct: multiProductPolicy }
      );
      const routingFlat = settingsToLegacyFields(routing, policyData);
      // OD + TP / OD only / TP only. Two insurers always sell both; the server derives the selection again.
      const coverage = policyData.companyChoice === "multiple" ? "both" : coverageOf(policyData);
      const selection = selectionOfCoverage(coverage);

      // Prepare the complete policy data for API
      const policyPayload = {
        // Basic Policy Details
        makeModel: policyData.makeModel,
        vehicleMake: vehicleMake,
        vehicleModel: vehicleModel,
        vehicleCC: vehicleCC,
        vehicleVariant: vehicleVariant || policyData.vehicleVariant || "",
        // Company routing. `settings` is the canonical object; company /
        // odCompany / tpCompany / paCoverCompany / paCoverInsurerCompany below
        // are derived from it so the automation's existing readers keep
        // working. The server rebuilds and validates it again.
        ...(routing.ODCompany || company
          ? { company: routing.ODCompany || company }
          : {}),
        rtoCityLocation: policyData.rtoCityLocation,
        RTORegion: policyData.RTORegion || "",
        RTOCity: policyData.RTOCity || "",
        idv: parseFloat(policyData.idv) || 0,
        // Model-not-found support. `false` and `0` survive the FormData
        // serializer (only undefined/null are dropped), so no `|| undefined`.
        modelNotFound: !!policyData.modelNotFound,
        // the IDV range / selling price below were set by hand in the IDV Calculator (this policy only)
        idvByHand: !!policyData.idvByHand,
        manufacturerSellingPrice:
          policyData.manufacturerSellingPrice === "" ||
            policyData.manufacturerSellingPrice === null ||
            policyData.manufacturerSellingPrice === undefined
            ? undefined
            : Number(policyData.manufacturerSellingPrice),
        idvRangeFrom:
          policyData.idvRangeFrom === "" || policyData.idvRangeFrom == null
            ? undefined
            : Number(policyData.idvRangeFrom),
        idvRangeTo:
          policyData.idvRangeTo === "" || policyData.idvRangeTo == null
            ? undefined
            : Number(policyData.idvRangeTo),
        // A 0% discount is legitimate. `|| undefined` dropped it from the
        // payload, and relianceForm.js then defaults a missing discount to 60
        // — so a deliberate 0 silently became 60 at the insurer portal.
        discount:
          policyData.discount === "" ||
            policyData.discount === null ||
            policyData.discount === undefined
            ? undefined
            : Number(policyData.discount),
        // The policy's own worth — motor premium plus CPA (with GST).
        finalPremium: resolvedTotalPremium,
        // Motor premium on its own, without the CPA.
        originalFinalPremium: resolvedMotorPremium,
        // walletDeductionAmount is NOT sent — the backend computes it itself
        // from paCoverAmount/paCoverCompany/company/Paymentmethod at the
        // moment of the atomic Company Wallet debit, so a client-sent figure
        // can never desync from what's actually charged.
        manufacturingYear: parseInt(policyData.manufacturingYear),
        manufacturingMonth: policyData.manufacturingMonth,
        purchaseDate: policyData.purchaseDate,
        registrationDate: policyData.registrationDate,
        zeroDepreciation: policyData.zeroDepreciation,
        zeroDepreciationPercentage: policyData.zeroDepreciationPercentage || 0,
        // Stored so the premium can be explained later: the base rate depends
        // on it, and an RTO's zone can change on the master afterwards.
        zone: policyData.zone || "",
        tppdRestrict: policyData.tppdRestrict,
        paCover: policyData.paCover,
        paCoverCompany: policyData.paCover
          ? routingFlat.paCoverCompany
          : policyData.paCoverCompany,
        paCoverAmount: parseFloat(policyData.paCoverAmount) || 0,
        notPaCoverReason: policyData.notPaCoverReason || "",

        // Product Selection — every policy generates both OD and TP. Which
        // insurer underwrites each, and who provides PA Cover, is `settings`
        // (below); it comes from the Make & Model master and the PA Cover
        // Company dropdown. schemaVersion marks every policy created by this
        // build of the form as using the new model; the backend flag
        // (ENABLE_MULTI_PRODUCT_POLICY, off by default) still gates whether it
        // acts on it. Brisk is not a separate product — it is PACompany.
        schemaVersion: 2,
        coverage,
        odSelected: selection.odSelected,
        tpSelected: selection.tpSelected,
        settings: routing,
        // Multiple company: the Combined Make & Model record. The server loads it,
        // checks it and sets both companies and the TP vehicle from it — only its
        // id is sent. `tpVehicleCC` is the CC the TP premium was priced on (the
        // server refuses a mismatch); `refreshTpVehicle` asks an edit to take the
        // TP row's latest values instead of keeping the ones the policy was sold with.
        ...comboPayloadFields({ policyData, calculationResult, isEditMode, savedSnapshot }),
        odCompany: routingFlat.odCompany,
        tpCompany: routingFlat.tpCompany,
        // Which insurer bills PA Cover when it is not Brisk ("" otherwise).
        paCoverInsurerCompany: routingFlat.paCoverInsurerCompany,

        userId: sessionStorage.getItem("UserId") || null,
        clientId: sessionStorage.getItem("clientID") || null,
        Paymentmethod: policyData.Paymentmethod || policyData.paymentMode || "wallet",
        netPremium: resolvedNetPremium ?? 0,
        // Full breakup, so the premium can be explained later without having to
        // re-run the calculator against masters that may since have changed.
        premiumCalculation: {
          finalPremium: resolvedMotorPremium ?? 0,
          netPremium: resolvedNetPremium ?? 0,
          tax: resolvedTax ?? 0,
          paOwnerDriver: paCoverBase,
          // Net (pre-GST) OD and TP shares of the motor premium, PA excluded.
          // Lets the wallet charge each insurer its own part when OD and TP are
          // different companies. Left out when the calculation is missing.
          ...(calculationResult?.totalAodPremium != null &&
            Number.isFinite(Number(calculationResult.totalAodPremium)) && {
            odPremium: Number(calculationResult.totalAodPremium),
            tpPremium: Number(
              (
                (Number(calculationResult.totalBodPremium) || 0) -
                (Number(calculationResult.pAOwnerDriver) || 0)
              ).toFixed(2)
            ),
          }),
        },
        // Financier Details (if Financier is selected)
        // Get financier data from customerData (step 1) first, fallback to policyData (step 0)
        hasFinancier: !!(
          customerData?.hasFinancier ?? policyData?.hasFinancier
        ),
        ...((customerData?.hasFinancier ?? policyData?.hasFinancier) && {
          financierType:
            customerData?.financierType || policyData?.financierType || "",
          financierName:
            customerData?.financierName || policyData?.financierName || "",
          financierAddress:
            customerData?.financierAddress ||
            policyData?.financierAddress ||
            "",
        }),

        // Nominee Details — sent whenever PA Cover is on (any insurer) OR the
        // company is National, which demands a nominee on every quote
        // regardless of PA Cover (see validationSchemas.js / national.js).
        // This used to be gated on paCover alone, so a National policy with
        // PA Cover off collected a nominee on screen but never sent it —
        // silently dropped before it reached onlinePolicy or the job queue.
        // National on EITHER leg counts (OD or TP): a Reliance-OD / National-TP
        // policy with PA off needs the nominee too.
        ...((policyData.paCover ||
          [routing.ODCompany, routing.TPCompany, company].some((c) => isNational(c))) && {
          nomineeName: policyData.nomineeName,
          nomineeRelation: policyData.nomineeRelation,
          otherRelationName: policyData.otherRelationName,
          appointeeName: policyData.appointeeName,
          nomineeDob: policyData.nomineeDob,
          nomineeGender: policyData.nomineeGender,
          nomineeAge: policyData.nomineeAge,
          paCoverYears: policyData.paCoverYears,
          // KSHEMA only. Fall back to the initial defaults rather than
          // undefined: an Autocomplete/TextField handed undefined flips from
          // controlled to uncontrolled and React drops the value silently.
          nomineeType: policyData.nomineeType || "individual",
          nomineeShare: policyData.nomineeShare ?? "",
        }),

        // Customer Details
        fullName: customerData.fullName,
        surname: customerData.surname,
        dateOfBirth: customerData.dateOfBirth,
        gender: customerData.gender,
        // Reliance's Title: the one chosen, else what the Gender implies, else Mr. — never empty, so the automation
        // always has something to select. (_titleChosen is UI-only and is not sent.)
        proposerTitle: titleToSend(customerData),
        fatherName: customerData.fatherName,
        // OCR-filled, no input field. "" rather than undefined so an edit of a
        // policy saved before this field existed still renders the read-only
        // tile instead of flipping it to an uncontrolled input.
        aadharNumber: customerData.aadharNumber || "",

        // Address Details
        flatDoorNo: customerData.flatDoorNo,
        floorNo: customerData.floorNo,
        buildingName: customerData.buildingName,
        blockName: customerData.blockName,
        roadStreetLane: customerData.roadStreetLane,
        areaAndLocality: customerData.areaAndLocality,
        state: customerData.state,
        country: customerData.country,
        pincode: customerData.pincode,
        email: customerData.email,
        mobileNumber: customerData.mobileNumber,

        // Vehicle Details
        engineNumber: customerData.engineNumber,
        chassisNumber: customerData.chassisNumber,

        // The customer's existing TP policy — Reliance's own-damage product asks it. OD only: required; two insurers:
        // optional, and the server sets the insurer to the TP company. "" = not entered (the server clears it).
        existingTpPolicyNumber: customerData.existingTpPolicyNumber || "",
        existingTpInsurer: customerData.existingTpInsurer || "",
        existingTpStartDate: customerData.existingTpStartDate || "",
        existingTpEndDate: customerData.existingTpEndDate || "",

        // Edit only: which parts (OD / TP / PA) the operator ticked to re-submit
        // on the last step. An empty list means "update only"; leaving it out
        // (a policy with no parts) keeps the server's own behaviour.
        ...(isEditMode &&
          Array.isArray(customerData?.resubmitParts ?? policyData?.resubmitParts) && {
          resubmitParts: customerData?.resubmitParts ?? policyData?.resubmitParts,
        }),

        // Edit only: who pays for this edit (asked when somebody other than the creator edits; the server
        // refunds whoever paid and charges this one), and whether the editor confirmed buying a bought part
        // again (the server then charges the full new amount once more and keeps the old payment).
        ...(isEditMode && {
          chargeTo: normalizeChargeTo(customerData?.chargeTo),
          rerunCompleted: boughtAgainKeys(
            editPolicyData?.parts,
            customerData?.resubmitParts ?? policyData?.resubmitParts,
            { paymentLink: String(policyData?.Paymentmethod || policyData?.paymentMode || "").toLowerCase() === "link" }
          ).length > 0,
        }),

        // Additional metadata
        policyType: "Online Policy",
        status: isEditMode
          ? editPolicyData?.status || "Incomplete"
          : "Incomplete",
        createdAt: isEditMode
          ? editPolicyData?.createdAt
          : new Date().toISOString(),
        updatedAt: isEditMode ? new Date().toISOString() : undefined,
        createdBy: isEditMode ? editPolicyData?.createdBy : "Online User",
        updatedBy: isEditMode ? "Online User" : undefined,
      };

      // Create FormData to send both policy data AND files in a single request
      const formData = new FormData();

      // Append all policy payload fields to FormData
      Object.keys(policyPayload).forEach((key) => {
        const value = policyPayload[key];
        if (value !== undefined && value !== null) {
          if (value instanceof File || value instanceof Blob) {
            // Files and Blobs are appended directly
            formData.append(key, value);
          } else if (value instanceof Date) {
            // Date objects are converted to ISO strings
            formData.append(key, value.toISOString());
          } else if (typeof value === "object") {
            // Objects/Arrays are stringified for FormData
            formData.append(key, JSON.stringify(value));
          } else {
            // Simple values (strings, numbers, booleans)
            formData.append(key, value);
          }
        }
      });

      if (!isEditMode && extras.createRequestId) formData.append("createRequestId", extras.createRequestId);
      if (!isEditMode && extras.holdForPayment) formData.append("holdForPayment", "true");

      // Append files if they exist (merged images: panCard and aadharCard)
      const hasPanCard =
        customerData.panCard instanceof File ||
        customerData.panCard instanceof Blob;
      const hasAadharCard =
        customerData.aadharCard instanceof File ||
        customerData.aadharCard instanceof Blob;

      if (hasPanCard) {
        console.log(
          "📤 Appending PAN Card to FormData:",
          customerData.panCard.name || "merged_pan.jpg"
        );
        formData.append("panCard", customerData.panCard);
      }
      if (hasAadharCard) {
        console.log(
          "📤 Appending Aadhar Card to FormData:",
          customerData.aadharCard.name || "merged_aadhar.jpg"
        );
        formData.append("aadharCard", customerData.aadharCard);
      }

      console.log(
        "Sending policy data and documents to API in a single request"
      );

      let response;
      if (isEditMode) {
        const updatePolicyId =
          editPolicyData?._id ||
          editPolicyData?.id ||
          editPolicyData?.policyId ||
          policyId;
        response = await PostCreateOnlinePolicy(formData, {
          run: "RERUN",
          rerunId: updatePolicyId,
        });
      } else {
        response = await PostCreateOnlinePolicy(formData);
      }
      lastResponse = response;

      // A wallet is short: the page asks "Cancel or hold until the money is added" (nothing was saved or charged).
      if (response && response.error && response.code === "INSUFFICIENT_WALLET" && !isEditMode && extras.onInsufficientWallet) {
        setOpenLoader(false);
        extras.onInsufficientWallet(response);
        return { error: true, insufficient: true };
      }

      if (response && !response.error) {
        const createdPolicyId = isEditMode
          ? editPolicyData?._id ||
          editPolicyData?.id ||
          editPolicyData?.policyId ||
          policyId
          : response.data?._id || response.data?.id;

        // Check for merging issues if no merged documents were provided but individual ones were
        if (
          !hasPanCard &&
          !hasAadharCard &&
          (customerData.panCardFront ||
            customerData.panCardBack ||
            customerData.aadharCardFront ||
            customerData.aadharCardBack)
        ) {
          console.warn("⚠️ Document merging might have failed.");
          ToastError(
            "Document merging failed. Please try re-uploading the documents."
          );
        }

        if (isEditMode) {
          loadAllOnlinePolicies();

          swal({
            title: "Policy Updated Successfully!",
            text: `Policy ID: ${createdPolicyId}`,
            icon: "success",
            buttons: true,
          });

          ToastSuccess("Policy updated successfully!");
          handleReset();
        } else {
          loadAllOnlinePolicies();

          if (response.heldForPayment) {
            // Saved ON HOLD: nothing charged, the automation waits for "Remove hold" (a warning stays until OK).
            swal({
              title: "Policy saved on hold",
              text: `Policy ID: ${response.data?.policyId || response.policyId || "Generated"}. ${response.message || ""}`.trim(),
              icon: "warning",
            });
          } else {
            swal({
              title: "Online Policy Created Successfully!",
              text: `Policy ID: ${response.data?.policyId || response.policyId || "Generated"
                }`,
              icon: "success",
              buttons: true,
            });

            ToastSuccess("Policy created successfully!");
          }

          // Learn-as-you-go: when Zero Depreciation was used and the Make &
          // Model row had no Nil Dip Price, save the figure the user typed back
          // to the master so the next policy for this vehicle fills itself in.
          //
          // Deliberately AFTER the policy is created and never awaited into the
          // failure path — the policy is already saved, so a failed master
          // update is a toast, not an error.
          if (
            policyData.zeroDepreciation &&
            policyData.makeModel &&
            policyData.zeroDepreciationPercentage !== "" &&
            policyData.zeroDepreciationPercentage !== null &&
            policyData.zeroDepreciationPercentage !== undefined &&
            !policyData._nilDipFromMaster
          ) {
            UpdateMakeModel(policyData.makeModel, {
              nilDipPrice: Number(policyData.zeroDepreciationPercentage),
            })
              .then(() =>
                ToastSuccess("Nil Dip Price saved to the Make & Model master")
              )
              .catch(() =>
                ToastError(
                  "Policy created, but the Nil Dip Price could not be saved to the Make & Model master."
                )
              );
          }

          // Close and reset the form after successful creation
          handleReset();
        }

        setOpenLoader(false);
        return { ok: true, heldForPayment: !!response.heldForPayment };
      } else {
        throw new Error(
          response?.message ||
          `Failed to ${isEditMode ? "update" : "create"} policy`
        );
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} policy:`,
        error
      );
      setOpenLoader(false);

      swal({
        title: `Error ${isEditMode ? "Updating" : "Creating"} Policy`,
        text:
          error.message ||
          `An error occurred while ${isEditMode ? "updating" : "creating"
          } the policy. Please try again.`,
        icon: "error",
        buttons: true,
      });

      ToastError(
        `Failed to ${isEditMode ? "update" : "create"
        } policy. Please try again.`
      );
      return { error: true, noResponse: !!(lastResponse && lastResponse.noResponse), code: lastResponse && lastResponse.code };
    }
  };

  return {
    showForm,
    setShowForm,
    selectedMakeModel,
    setSelectedMakeModel,
    selectedCombo,
    setSelectedCombo,
    calculationResult,
    setCalculationResult,
    showCalculationResult,
    setShowCalculationResult,
    policyData,
    setPolicyData,
    activeStep,
    setActiveStep,
    isEditMode,
    setIsEditMode,
    isViewMode,
    setViewMode,
    editPolicyData,
    setEditPolicyData,
    savedSnapshot,
    setSavedSnapshot,
    releaseSaved,
    handleNext,
    handleBack,
    handleReset,
    handleViewPolicy,
    handleViewPolicyErrorLogs,
    handleViewPolicyHistory,
    saveCustomerDetails,
  };
};
